import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  CreditCard,
  DollarSign,
  Wallet,
  Send,
  Link2,
  ArrowDownToLine,
  Receipt,
  ClipboardList,
  BarChart3,
  TrendingUp,
  Coins,
  BookOpen,
  BadgeDollarSign,
  Users,
  LogOut,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { initials } from "../lib/format";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, perm: "dashboard", end: true },
  { to: "/daily-profit", label: "Daily Profit", icon: Coins, perm: "dashboard" },
  { to: "/chimes", label: "Chimes", icon: CreditCard, perm: "chimes" },
  { to: "/cashapps", label: "CashApp", icon: DollarSign, perm: "cashapps" },
  { to: "/paypals", label: "PayPal", icon: Wallet, perm: "paypals" },
  { to: "/venmos", label: "Venmo", icon: Send, perm: "venmos" },
  { to: "/links", label: "Links", icon: Link2, perm: "links" },
  { to: "/withdrawals", label: "Withdrawals", icon: ArrowDownToLine, perm: "withdrawals" },
  { to: "/usdt-sent", label: "USDT Sent", icon: Send, perm: "usdt_sent" },
  { to: "/cash-coin-payments", label: "Cash Coin Payment", icon: Coins, perm: "cash_coin_payments" },
  { to: "/party-book", label: "Party Book", icon: BookOpen, perm: "usdt_sent" },
  { to: "/team-credit", label: "Teams Credit", icon: BadgeDollarSign, perm: "team_credit" },
  { to: "/tron-usdt", label: "TRON USDT", icon: Link2, perm: "usdt_sent" },
  { to: "/bsc-usdt", label: "BSC USDT", icon: Link2, perm: "usdt_sent" },
  { to: "/expenses", label: "Expenses", icon: Receipt, perm: "expenses" },
  { to: "/salaries", label: "EM Salary Sheets", icon: ClipboardList, perm: "salaries" },
  { to: "/team-analysis", label: "Team Analysis", icon: BarChart3, perm: "team_analysis" },
  { to: "/net-profits", label: "Net Profits", icon: TrendingUp, perm: "net_profits" },
  { to: "/net-profit-analytics", label: "Net Profit Analytics", icon: BarChart3, perm: "net_profits" },
  { to: "/admins", label: "Admins & Roles", icon: Users, perm: "admins" },
];

export default function Sidebar() {
  const { user, logout, hasPermission } = useAuth();
  const navigate = useNavigate();

  const onLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <aside
      className="hidden md:flex w-64 shrink-0 flex-col border-r hairline bg-[var(--sidebar)]"
      data-testid="app-sidebar"
    >
      <div className="px-6 pt-7 pb-6">
        <div className="flex items-center gap-2">
          <div className="h-7 w-7 rounded-sm bg-zinc-900 text-white flex items-center justify-center font-display text-[13px] font-bold">
            CF
          </div>
          <span className="font-display text-[15px] font-bold tracking-tight">
            Central Finance
          </span>
        </div>
        <p className="text-[11px] text-zinc-500 mt-1 uppercase tracking-widest">
          Ops Control
        </p>
      </div>

      <nav className="flex-1 px-3 space-y-0.5 thin-scroll overflow-y-auto">
        {NAV.map(({ to, label, icon: Icon, perm, end }) => {
          const allowed = hasPermission(perm);
          if (!allowed) return null;
          // CF Team users: hide Dashboard (they only see Daily Profit)
          if (user?.cf_team && to === "/") return null;
          return (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={`nav-${label.toLowerCase().replace(/[^a-z]/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-md text-[13.5px] transition-colors ${
                  isActive
                    ? "bg-zinc-900 text-white"
                    : "text-zinc-700 hover:bg-zinc-200/60"
                }`
              }
            >
              <Icon size={15} strokeWidth={1.6} />
              <span>{label}</span>
            </NavLink>
          );
        })}
      </nav>

      <div className="border-t hairline px-4 py-4">
        <div className="flex items-center gap-3">
          <div className="h-8 w-8 shrink-0 rounded-full bg-zinc-900 text-white flex items-center justify-center text-[11px] font-semibold">
            {initials(user?.name)}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[13px] font-medium truncate" data-testid="sidebar-user-name">
              {user?.name}
            </div>
            <div className="text-[11px] text-zinc-500 truncate">
              {user?.is_master ? "Master Admin" : user?.role?.name || "No role"}
            </div>
          </div>
          <button
            onClick={onLogout}
            className="p-1.5 text-zinc-500 hover:text-zinc-900 hover:bg-zinc-200/60 rounded"
            title="Log out"
            data-testid="logout-btn"
          >
            <LogOut size={15} strokeWidth={1.6} />
          </button>
        </div>
      </div>
    </aside>
  );
}
